import { usePeople, type People } from '@/hooks/usePeople'
import { useWhosePicker, type WhosePicker } from '@/hooks/useWhosePicker'
import { whoseChoices, whoseLabel, type WhoseFilter } from '@/logic/filters'
import { usePrefs } from '@/store/prefs'
import { Avatar, PairAvatars } from './Avatar'
import { LockIcon, UnlockIcon } from './icons'
import { Segmented } from './Segmented'
import './Whose.css'

// Whose tasks are showing, one tap from the top bar. The button always shows
// the choice that is on (ALL, my letter, the other person's, or both for
// Shared) and opens All · Mine · Begüm's · Shared; picking one closes it. On a
// phone the bar opens as a row under the top row, above the tray; on a wide
// screen it drops down under the button. It sets the same app-wide filter as
// before, so the Plan tab, every Calendar mode and search follow it.
// While Shared is on, a small lock keeps my private tasks out (shut, as it
// starts) or lets them in (open). A wide screen has it beside the button; a
// phone's top row has no room, so it sits at the end of the bar, and the
// button carries a small open lock while my private tasks are showing.

function Face({ whose, people }: { whose: WhoseFilter; people: People }) {
  if (whose === 'mine') return <Avatar uid={people.meUid} people={people} />
  if (whose === 'partner' && people.other) return <Avatar uid={people.other.uid} people={people} />
  if (whose === 'shared') return <PairAvatars people={people} />
  return <span className="whose__all">ALL</span>
}

/** The avatars in the top row: what is showing, and the way to change it. */
export function WhoseButton({ picker, lockBeside = false }: { picker: WhosePicker; lockBeside?: boolean }) {
  const people = usePeople()
  const whose = usePrefs((s) => s.filter.whose)
  const withPrivate = usePrefs((s) => s.filter.whose === 'shared' && s.filter.sharedWithPrivate === true)
  const label = whoseLabel(whose, people.other?.name ?? null)
  return (
    <button
      data-whose-picker=""
      type="button"
      className={`whose__btn ${label !== 'All' ? 'whose__btn--on' : ''}`}
      aria-label={`Whose tasks: ${label}${withPrivate ? ', with my private tasks' : ''}`}
      aria-expanded={picker.open}
      onClick={picker.toggle}
    >
      <span className="whose__face" aria-hidden="true">
        <Face whose={label === 'All' ? null : whose} people={people} />
        {withPrivate && !lockBeside && (
          <span className="whose__badge">
            <UnlockIcon size={10} weight={2.6} />
          </span>
        )}
      </span>
    </button>
  )
}

/** Beside the Whose button while Shared is on: open, my private tasks show with the shared ones. */
export function SharedLock() {
  const shared = usePrefs((s) => s.filter.whose === 'shared')
  const open = usePrefs((s) => s.filter.sharedWithPrivate === true)
  const setFilter = usePrefs((s) => s.setFilter)
  if (!shared) return null
  return (
    <button
      type="button"
      className={`whose__lock ${open ? 'whose__lock--open' : ''}`}
      aria-pressed={open}
      aria-label="Show my private tasks"
      title={open ? 'Showing your private tasks too' : 'Your private tasks are hidden'}
      onClick={() => setFilter({ sharedWithPrivate: !open })}
    >
      {open ? <UnlockIcon size={18} /> : <LockIcon size={18} />}
    </button>
  )
}

/** All · Mine · Begüm's · Shared. Picking one sets the filter and closes the bar. */
export function WhoseBar({ picker, drop = false }: { picker: WhosePicker; drop?: boolean }) {
  const people = usePeople()
  const whose = usePrefs((s) => s.filter.whose)
  const setFilter = usePrefs((s) => s.setFilter)
  if (!picker.open) return null
  const choices = whoseChoices(people.other?.name ?? null).map((c) => ({ value: c.value ?? ('all' as const), label: c.label }))
  return (
    <div data-whose-picker="" className={drop ? 'whose__drop' : 'whose__bar'}>
      <Segmented
        label="Whose tasks"
        fill={!drop}
        value={whose ?? 'all'}
        options={choices}
        onChange={(v) => {
          setFilter({ whose: v === 'all' ? null : v })
          picker.close()
        }}
      />
      {!drop && <SharedLock />}
    </div>
  )
}

/** A wide screen's picker: the button, with the bar dropping down under it, and the lock beside it. */
export function WhoseDropdown() {
  const picker = useWhosePicker()
  return (
    <>
      <span className="whose__wrap">
        <WhoseButton picker={picker} lockBeside />
        <WhoseBar picker={picker} drop />
      </span>
      <SharedLock />
    </>
  )
}
